import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

export interface TurretConfig extends BehaviorConfig {
  rotateSpeed?: number;       // Degrees per second (default: 180)
  range?: number;             // Detection range (default: uses detectionRange from prefab)
  aimTolerance?: number;      // Degrees tolerance to consider "aiming" (default: 5)
}

/**
 * TurretBehavior - Rotating aiming for turret-type entities
 *
 * Used by: Cannoncannon, cañon_rojo, cañon_azul
 *
 * Features:
 * - Physical rotation toward target (player)
 * - Exposes isAiming for ShooterBehavior to check
 * - Configurable rotation speed and aim tolerance
 *
 * Note: This behavior only handles rotation. Shooting is handled by ShooterBehavior,
 * which checks turret.isAiming before firing.
 */
export class TurretBehavior extends Behavior {
  /** True when turret is aimed at target within tolerance */
  public isAiming: boolean = false;

  private rotateSpeed: number;
  private range: number;
  private aimTolerance: number;

  constructor(entity: Entity, config: TurretConfig = {}) {
    super(entity, config);

    this.rotateSpeed = config.rotateSpeed ?? 180;
    this.range = config.range ?? 700;
    this.aimTolerance = config.aimTolerance ?? 5;

    // Initialize namespaced state for behavior communication
    this.entity.state.behaviors.turret = { isAiming: false, angle: 0 };
  }

  /**
   * Update - rotates turret toward player if in range
   */
  override update(_time: number, delta: number): void {
    if (!this.enabled) return;

    const player = this.scene.registry.get('player') as Entity | undefined;
    if (!player || !player.active) {
      this.isAiming = false;
      this.entity.state.behaviors.turret!.isAiming = false;
      return;
    }

    // Calculate distance to player
    const distance = Phaser.Math.Distance.Between(
      this.entity.x, this.entity.y,
      player.x, player.y
    );

    // Check if player is in detection range
    if (distance > this.range) {
      this.isAiming = false;
      this.entity.state.behaviors.turret!.isAiming = false;
      return;
    }

    // Calculate angle toward player
    const targetAngle = Phaser.Math.RadToDeg(
      Phaser.Math.Angle.Between(this.entity.x, this.entity.y, player.x, player.y)
    );

    // Rotate toward target
    this.rotateTowards(targetAngle, delta);

    // Update aiming state
    this.isAiming = this.isAimingAt(targetAngle);

    // Update shared state for other behaviors (e.g., ShooterBehavior)
    this.entity.state.behaviors.turret!.isAiming = this.isAiming;
    this.entity.state.behaviors.turret!.angle = Phaser.Math.DegToRad(this.entity.angle);
  }

  /**
   * Rotate entity toward target angle at configured speed
   */
  private rotateTowards(targetAngle: number, delta: number): void {
    const currentAngle = this.entity.angle;
    const diff = Phaser.Math.Angle.ShortestBetween(currentAngle, targetAngle);
    const maxRotation = this.rotateSpeed * (delta / 1000);

    if (Math.abs(diff) <= maxRotation) {
      this.entity.setAngle(targetAngle);
    } else {
      this.entity.setAngle(currentAngle + (diff > 0 ? maxRotation : -maxRotation));
    }
  }

  /**
   * Check if turret is aimed at target within tolerance
   */
  private isAimingAt(targetAngle: number): boolean {
    const diff = Math.abs(Phaser.Math.Angle.ShortestBetween(this.entity.angle, targetAngle));
    return diff <= this.aimTolerance;
  }

  /**
   * Get current aim angle in degrees
   */
  getAimAngle(): number {
    return this.entity.angle;
  }

  /**
   * Set rotation speed (degrees per second)
   */
  setRotateSpeed(speed: number): void {
    this.rotateSpeed = speed;
  }

  /**
   * Set detection range
   */
  setRange(range: number): void {
    this.range = range;
  }

  /**
   * Set aim tolerance (degrees)
   */
  setAimTolerance(tolerance: number): void {
    this.aimTolerance = tolerance;
  }

  /**
   * Clean up state on destroy
   */
  override destroy(): void {
    delete this.entity.state.behaviors.turret;
    super.destroy();
  }
}
